import { css } from '@emotion/react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { faBook } from '@inflearn/pro-regular-svg-icons';

import { useLang } from '@inflearn/i18n-tools';
import { Trans, useTranslation } from 'next-i18next';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import StartInstructorButton from './StartInstructorButton';
import { JOIN_INSTRUCTOR_CONTENT_TYPES, JOIN_INSTRUCTOR_SPOTS } from './constants/gtmEventParams';

const INFLEARN_GUIDE_LINK_MAP = {
  en: 'https://slashpage.com/inflearn-instructor-guide-en',
  // vi 용은 아직 없음
  vi: 'https://slashpage.com/inflearn-instructor-guide-en',
  ja: 'https://slashpage.com/inflearninstructorguidejp',
  ko: 'https://inflab-1.gitbook.io/inflearn',
} as const;

const CtaSection = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  return (
    <Box component="section" css={styleSection}>
      <Flex
        direction="column"
        align="center"
        styles={{
          root: styleContainer,
        }}
      >
        <Stack
          gap={48}
          align="center"
          styles={{
            root: styleContents,
          }}
        >
          <Stack
            gap={16}
            styles={{
              root: styleTextStack,
            }}
          >
            <Text
              c="gray.9"
              fz={56}
              fw={700}
              lh={1.3}
              styles={{
                root: styleHeading,
              }}
            >
              <Trans
                i18nKey="open.knowledge.cta.heading"
                components={{
                  green: <span css={styleGreenText} />,
                  br: <br />,
                }}
              />
            </Text>
            <Box css={styleSubText}>
              <Title
                order={3}
                c="dark.9"
                fz={22}
                fw={700}
                lh={1.5}
                styles={{
                  root: styleSubBold,
                }}
              >
                {t('open.knowledge.cta.subtitle')}
              </Title>
              <Text
                c="gray.9"
                fz={22}
                fw={500}
                styles={{
                  root: styleSubNormal,
                }}
              >
                {t('open.knowledge.cta.description')}
              </Text>
            </Box>
          </Stack>
          <Flex
            gap={12}
            styles={{
              root: styleButtonGroup,
            }}
          >
            {INFLEARN_GUIDE_LINK_MAP[lang] ? (
              <Button
                component="a"
                href={INFLEARN_GUIDE_LINK_MAP[lang]}
                variant="default"
                target="_blank"
                rel="noopener noreferrer"
                color="infgreen"
                size="lg"
                radius="xl"
                rightSection={<Icon icon={faBook} size="lg" color="gray.9" />}
                miw={240}
                h={56}
                onClick={() =>
                  trackMixpanelEvent(eventNames.CLICK_JOIN_INSTRUCTOR_CONTENT, {
                    content_type: JOIN_INSTRUCTOR_CONTENT_TYPES.GUIDE_DOCS,
                  })
                }
              >
                {t('open.knowledge.cta.guide')}
              </Button>
            ) : null}
            <StartInstructorButton
              spot={JOIN_INSTRUCTOR_SPOTS.FINAL_CTA}
              miw={240}
              h={56}
              variant="filled"
              color="dark"
            >
              {t('open.knowledge.cta.start')}
            </StartInstructorButton>
          </Flex>
        </Stack>
      </Flex>
    </Box>
  );
};

export default CtaSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f8f9fa',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleTextStack = css({
  width: '100%',
  textAlign: 'center',
});

const styleHeading = css({
  fontSize: 56,
  fontWeight: 700,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.tablet]: {
    fontSize: 44,
  },
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 28,
  },
});

const styleGreenText = css({
  color: '#00a760',
});

const styleSubText = css({
  textAlign: 'center',
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubBold = css({
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubNormal = css({
  fontWeight: 400,
  fontSize: 22,
  lineHeight: 1.5,
  wordBreak: 'keep-all',
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleButtonGroup = css({
  [desktopFirstMediaQuery.mobile]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
});
